import React, { createContext, useContext, useEffect, useState } from "react";
import { useAuthProvider } from "../auth/hooks/auth-provider";
import { LanguageCode, toLanguageCode } from "../models/enums/LanguageCode";
import { useCurrentUser } from "../hooks/useUser";
import i18n from "./i18n";

interface LanguageContextType {
	currentLanguage: LanguageCode;
	changeLanguage: (lang: LanguageCode) => void;
}

const LanguageContext = createContext<LanguageContextType | undefined>(undefined);

export const LanguageProvider: React.FC<{ children: React.ReactNode }> = ({
	children
}) => {
	// Normalise to a canonical LanguageCode: i18n.language is lowercase ("fr"/"en") while the enum
	// and every translation `values`/flags key are capitalised. Leaving currentLanguage lowercase
	// leaked into useTranslatableForm's editingLanguage and made values[editingLanguage] miss.
	const [currentLanguage, setCurrentLanguage] = useState(() => toLanguageCode(i18n.language));
	const { isAuthenticated } = useAuthProvider();
	const { data: currentUser } = useCurrentUser(isAuthenticated);

	useEffect(() => {
		if (!isAuthenticated || !currentUser) return;

		// Use preferred language from user settings (fetched from API)
		changeLanguage(currentUser.settings?.preferredLanguage ?? LanguageCode.En);
	}, [isAuthenticated, currentUser]);

	const changeLanguage = (lang: LanguageCode) => {
		const normalized = toLanguageCode(lang);
		i18n.changeLanguage(normalized.toLowerCase());
		setCurrentLanguage(normalized);
	};

	return (
		<LanguageContext.Provider value={{ currentLanguage, changeLanguage }}>
			{children}
		</LanguageContext.Provider>
	);
};

export const useLanguage = (): LanguageContextType => {
	const context = useContext(LanguageContext);
	if (!context) {
		throw new Error("useLanguage must be used within a LanguageProvider");
	}
	return context;
};
